import uniqueId from 'lodash/uniqueId'
import { useState } from 'react'
import { Size } from '@opallabs/gem'
import { Icon } from '@opallabs/lapidary'
import { kuiperClassNames } from '../utils'
import * as styles from './styles'
import './styles.scss'

type ThumbSingleSelectItem = Record<string, unknown> & {
  title: string
  description?: string
}

type ThumbSingleSelectProps<TItem> = {
  children: ({ item, index }: { item: TItem; index: number }) => React.ReactNode
  className?: string
  defaultSelection?: number
  items: TItem[]
  onChange?: (selectedIndex: number) => void
}

type ThumbSelectableProps = {
  description?: string
  onClick: () => void
  selected?: boolean
  title: string
}

const ThumbSelectable: React.FC<
  React.PropsWithChildren<ThumbSelectableProps>
> = ({ description, children, onClick, selected = false, title }) => {
  const [componentId] = useState(uniqueId('kuiper-thumbSelectable-'))
  const theme = 'dark'

  const footer = (
    <div className={kuiperClassNames('selectable-footer')}>
      <div
        id={`${componentId}-title`}
        className={kuiperClassNames('select-title')}
      >
        {title}
      </div>
      {description && <p id={`${componentId}-description`}>{description}</p>}
    </div>
  )

  if (selected) {
    return (
      <div className={kuiperClassNames('selectable-wrapper')}>
        <div
          onClick={onClick}
          onKeyDown={onClick}
          role="button"
          tabIndex={0}
          aria-pressed={true}
          className={kuiperClassNames('selectable', 'single-selected')}
          aria-labelledby={`${componentId}-title`}
          aria-describedby={`${componentId}-description`}
        >
          {children}
          <div
            className={kuiperClassNames('checkbox-wrapper')}
            css={styles.iconWrapper({ theme })}
          >
            <Icon
              className={kuiperClassNames('selected-checkmark')}
              glyph="gem-check-circle"
              width={Size.Icon * 2}
              height={Size.Icon * 2}
              ariaLabel="Selected theme"
              styleOverrides={{
                container: styles.icon({ theme }),
              }}
            />
          </div>
        </div>
        {footer}
      </div>
    )
  }
  return (
    <div className={kuiperClassNames('selectable-wrapper')}>
      <div
        className={kuiperClassNames('selectable')}
        onClick={onClick}
        onKeyDown={onClick}
        role="button"
        tabIndex={0}
        aria-pressed={false}
        aria-labelledby={`${componentId}-title`}
        aria-describedby={`${componentId}-description`}
      >
        {children}
      </div>
      {footer}
    </div>
  )
}

/**
 * This component uses a render-prop pattern to display its items.
 * Each item is wrapped by the <ThumbSelectable> component which
 * adds a button around the component you provide.
 *
 * Item objects MUST have a "title" property, and MAY have a
 * "description" property. You can also include any other properties
 * necessary to render your component.
 *
 * @example
 * <ThumbSingleSelect
 *  items={[
 *    { title: "Item 1", glyph: "icon-circle" },
 *    { title: "Item 2", glyph: "icon-square" },
 *  ]}
 * >
 *  {({ item }) => (
 *    <Icon glyph={item.glyph} ariaLabel={item.title} />
 *  )}
 * </ThumbSingleSelect>
 */
const ThumbSingleSelect = <TItem extends ThumbSingleSelectItem>({
  children,
  className,
  defaultSelection = 0,
  items,
  onChange,
}: ThumbSingleSelectProps<TItem>) => {
  const [selectedIndex, setSelectedIndex] = useState(defaultSelection)

  return (
    <div
      className={kuiperClassNames('thumb-single-select', className)}
      css={styles.container}
    >
      {items.map((item, index) => (
        <ThumbSelectable
          key={index}
          description={item.description}
          onClick={() => {
            setSelectedIndex(index)
            onChange?.(index)
          }}
          selected={index === selectedIndex}
          title={item.title}
        >
          {children({ item, index })}
        </ThumbSelectable>
      ))}
    </div>
  )
}

ThumbSingleSelect.displayName = 'Kuiper: ThumbSingleSelect'
export default ThumbSingleSelect
